import React, { ReactNode, useState } from 'react';
import { CalendarDays, CircleGauge, FileText, FlaskConical, Globe2, Lightbulb, ListChecks, PackageCheck, ScanSearch } from 'lucide-react';
import { Dossier } from '../../data/mockData';
import { ProductRecordConfig } from '../../data/productRecords';
import { Market } from '../ui/MarketSelector';
import { ReadinessRing } from '../ui/ReadinessRing';
import { CompareGate } from './CompareControls';
import { MarketChip, MarketChipRow } from './MarketChipRow';
import { FormulaCompareView } from './FormulaCompareView';
import { ClaimsCompareView } from './ClaimsCompareView';
export type ProductRecordTabId = 'overview' | 'dossier' | 'formula' | 'nip' | 'claims' | 'suggested-claims' | 'label';
interface ProductRecordShellProps {
  dossier: Dossier;
  record: ProductRecordConfig;
  market: Market;
  markets: Market[];
  marketChips: MarketChip[];
  onMarketChange: (market: Market) => void;
  activeTab: ProductRecordTabId;
  onTabChange: (tab: ProductRecordTabId) => void;
  children: ReactNode;
}
const tabs: {
  id: ProductRecordTabId;
  label: string;
  icon: typeof ScanSearch;
}[] = [{
  id: 'overview',
  label: 'Overview',
  icon: ScanSearch
}, {
  id: 'dossier',
  label: 'Product Dossier',
  icon: FileText
}, {
  id: 'formula',
  label: 'Formula',
  icon: FlaskConical
}, {
  id: 'nip',
  label: 'NIP',
  icon: CircleGauge
}, {
  id: 'claims',
  label: 'Claims',
  icon: ListChecks
}, {
  id: 'suggested-claims',
  label: 'Suggested claims',
  icon: Lightbulb
}, {
  id: 'label',
  label: 'Packaging & Label',
  icon: PackageCheck
}];
export function ProductRecordShell({
  dossier,
  record,
  market,
  markets,
  marketChips,
  onMarketChange,
  activeTab,
  onTabChange,
  children
}: ProductRecordShellProps) {
  const [showCompareGate, setShowCompareGate] = useState(false);
  const [showFormulaCompare, setShowFormulaCompare] = useState(false);
  const [showClaimsCompare, setShowClaimsCompare] = useState(false);
  const supportsCompare = (activeTab === 'formula' || activeTab === 'claims') && record.supportsCompare !== false;
  if (showFormulaCompare) {
    return <FormulaCompareView productName={dossier.name} variant={record.hasNip ? 'food' : 'supplement'} onBack={() => setShowFormulaCompare(false)} data-id="element-2307" data-mp="35r27ydefftl5" />;
  }
  if (showClaimsCompare) {
    return <ClaimsCompareView productName={dossier.name} variant={record.hasNip ? 'food' : 'supplement'} onBack={() => setShowClaimsCompare(false)} data-id="element-2308" data-mp="3figbcw9i1djk" />;
  }
  return <div className="flex flex-1 flex-col overflow-hidden bg-[#fafbf8]" data-id="element-2309" data-mp="2mtrbf0k6je9e">
      <div className="flex-1 overflow-y-auto px-6 py-7 lg:px-10" data-id="element-2310" data-mp="2hvpqsb73v6vt">
        <div className="mx-auto max-w-7xl" data-id="element-2311" data-mp="2hxaouasmp7yf">
          <section className="rounded-2xl border border-[#dce3da] bg-white px-6 py-6 shadow-sm sm:px-8" aria-labelledby="product-record-title" data-id="element-2312" data-mp="2wt9sn98pglxr">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center" data-id="element-2313" data-mp="2rscagwnp3rw9">
              <ReadinessRing score={dossier.readiness} size={100} label="Product readiness" className="shrink-0" data-id="element-2314" data-mp="c16karsyzvzm" />
              <div className="min-w-0" data-id="element-2315" data-mp="72wm08by8f9s">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-[#728176]" data-id="element-2316" data-mp="emxliwva9jhq">Product dossier · readiness</p>
                <h1 id="product-record-title" className="mt-2 truncate font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen sm:text-3xl" data-id="element-2317" data-mp="jn1yicwzfw37">
                  {dossier.name}
                </h1>
                <div className="mt-4 flex flex-wrap gap-2" aria-label="Product metadata" data-id="element-2318" data-mp="9oma4hpcb0xe">
                  <MetadataPill data-id="element-2319" data-mp="xegl0tq3mff0">{record.pills.category}</MetadataPill>
                  <MetadataPill icon={<Globe2 className="h-3.5 w-3.5" data-id="element-2321" data-mp="12crv95kb2rxr" />} data-id="element-2320" data-mp="12c8n2gkgjhwx">{record.pills.markets}</MetadataPill>
                  <MetadataPill icon={<FileText className="h-3.5 w-3.5" data-id="element-2323" data-mp="ok24kntu0yub" />} data-id="element-2322" data-mp="olmziek26jsp">{record.pills.documents}</MetadataPill>
                  <MetadataPill icon={<CalendarDays className="h-3.5 w-3.5" data-id="element-2325" data-mp="sl5nxkb39d0a" />} data-id="element-2324" data-mp="slngo6yjl93g">{record.pills.date}</MetadataPill>
                </div>
              </div>
            </div>
          </section>

          <MarketChipRow caption="Readiness by market" chips={marketChips} selected={market.code} onSelect={code => {
          setShowCompareGate(false);
          const next = markets.find(entry => entry.code === code);
          if (next) onMarketChange(next);
        }} onLocked={() => setShowCompareGate(true)} onAdd={() => setShowCompareGate(true)} onCompare={supportsCompare ? () => activeTab === 'formula' ? setShowFormulaCompare(true) : setShowClaimsCompare(true) : undefined} data-id="element-2326" data-mp="1l39i4tigitb7" />

          <div className="mt-4 flex flex-col border-b border-[#dce3da] bg-white lg:flex-row lg:items-center lg:justify-between" data-id="element-2327" data-mp="b74ddl6i9pi1">
            <nav className="flex min-w-0 overflow-x-auto px-2" role="tablist" aria-label="Product record sections" data-id="element-2328" data-mp="6djoqjqsy7vi">
              {tabs.filter(tab => tab.id !== 'nip' || record.hasNip || record.showNip).map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} type="button" role="tab" aria-selected={isActive} onClick={() => {
                setShowCompareGate(false);
                onTabChange(tab.id);
              }} className={`relative inline-flex shrink-0 items-center gap-2 px-4 py-4 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-green ${isActive ? 'text-evergreen' : 'text-[#738478] hover:text-evergreen'}`} data-id="element-2329" data-mp="2crypw251491u">
                    <Icon className="h-4 w-4" strokeWidth={1.8} data-id="element-2330" data-mp="35iqikpmvyfkf" />
                    {tab.label}
                    {isActive && <span className="absolute inset-x-4 bottom-0 h-0.5 rounded-full bg-evergreen" data-id="element-2331" data-mp="3eh2ixjdk0rq2" />}
                  </button>;
            })}
            </nav>
          </div>

          <div className="py-6" data-id="element-2332" data-mp="3jdsz06pcznbh">
            {supportsCompare && showCompareGate ? <CompareGate onAddMarket={() => setShowCompareGate(false)} data-id="element-2333" data-mp="1je5sa6ia7e4y" /> : children}
          </div>
        </div>
      </div>
    </div>;
}
interface MetadataPillProps {
  children: ReactNode;
  icon?: ReactNode;
}
function MetadataPill({
  children,
  icon
}: MetadataPillProps) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-[#dbe3d8] bg-[#f0f4ed] px-3 py-1.5 text-sm font-medium text-evergreen" data-id="element-2334" data-mp="1obec0bj2vq8b">
      {icon}
      {children}
    </span>;
}